import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import {
  Button,
  Dialog,
  Input,
  Select,
  Toast,
  useInput,
  useSelect,
  useViewport
} from '@chilecompra/react-kit/components'
import { useLanguage, useLanguageFlags } from '@chilecompra/react-kit/i18n'
import {
  ADDRESS_CHECK,
  checkInputErrorRegEx,
  checkInputErrorRequired,
  formatRegEx,
  ID_CHECK,
  ID_FORMAT,
  NAME_CHECK
} from '@chilecompra/react-kit/utils'
import { FOREIGN_BUSINESS_SIZES_OPTIONS, FOREIGN_BUSINESS_TYPES_OPTIONS } from '../../config/settings/constants'
import {
  REACT_APP_HELP_URL,
  REACT_APP_MERCADO_PUBLICO_BASE_URL,
  REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL
} from '../../config/settings/environment'
import { checkInputErrorMaxLength } from '../../modules/utils/checkers'
import {
  onCheckGuestBusinessSuccess,
  onCleanGuestBusinessErrorsSuccess,
  onCreateTemporarySellerThunk,
  onGetSellerBlockedInfoThunk
} from './RegisterWizard.actions'
import {
  DesktopNavigation,
  ExternalLink,
  Instructions,
  LinkHelp,
  MobileNavigation,
  ModalAction,
  ModalContent,
  ModalInstructions,
  ModalText,
  ModalTitle,
  Root,
  Section,
  Strong,
  Title,
  WarningContainer,
  WarningIcon
} from './RegisterWizardForeignBusinessInformation.styles'

/**
 * The RegisterWizardForeignBusinessInformation's container.
 */
const RegisterWizardForeignBusinessInformation = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { blockedInfo, countries, errors, loading } = useSelector(state => state.registerWizard)
  const { language } = useLanguage()
  const { hasLanguage, isLanguageEN } = useLanguageFlags()
  const { size } = useViewport()
  const {
    error: nameError,
    value: nameValue,
    onChange: handleNameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignBusinessInformation__fieldRequiredError')),
      checkInputErrorRegEx(NAME_CHECK, language('RegisterWizardForeignBusinessInformation__fieldInvalidError'))
    ]
  })
  const {
    error: nicknameError,
    value: nicknameValue,
    onChange: handleNicknameChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignBusinessInformation__fieldRequiredError')),
      checkInputErrorRegEx(NAME_CHECK, language('RegisterWizardForeignBusinessInformation__fieldInvalidError'))
    ]
  })
  const {
    error: dniError,
    value: dniValue,
    onChange: handleDniChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignBusinessInformation__fieldRequiredError')),
      checkInputErrorRegEx(ID_CHECK, language('RegisterWizardForeignBusinessInformation__fieldInvalidError')),
      checkInputErrorMaxLength(20, language('RegisterWizardForeignBusinessInformation__fieldDniMaxLengthError'))
    ],
    formatCallbacks: [value => formatRegEx(value, ID_FORMAT)]
  })
  const { value: typeValue, onChange: handleTypeChange } = useSelect({ initialValue: 22 })
  const { value: sizeValue, onChange: handleSizeChange } = useSelect({ initialValue: 1 })
  const {
    error: countryError,
    value: countryValue,
    onChange: handleCountryChange
  } = useSelect({
    errorCallbacks: [checkInputErrorRequired(language('RegisterWizardForeignBusinessInformation__fieldRequiredError'))]
  })
  const {
    error: addressError,
    value: addressValue,
    onChange: handleAddressChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(language('RegisterWizardForeignBusinessInformation__fieldRequiredError')),
      checkInputErrorRegEx(ADDRESS_CHECK, language('RegisterWizardForeignBusinessInformation__fieldInvalidError'))
    ]
  })
  const businessTypeOptions = useMemo(
    () => FOREIGN_BUSINESS_TYPES_OPTIONS.map(option => ({ ...option, name: language(option.name) || option.name })),
    [isLanguageEN]
  )
  const businessSizeOptions = useMemo(
    () => FOREIGN_BUSINESS_SIZES_OPTIONS.map(option => ({ ...option, name: language(option.name) || option.name })),
    [isLanguageEN]
  )
  const businessCountriesOptions = useMemo(
    () =>
      countries
        ?.filter(option => option.code !== 'CL')
        .map(option => ({ name: isLanguageEN ? option.englishName : option.spanishName, value: option.id } || [])),
    [countries, isLanguageEN]
  )
  const handleDialogClose = () => dispatch(onCleanGuestBusinessErrorsSuccess())
  const handleDesktopRedirect = () => window.open(REACT_APP_MERCADO_PUBLICO_BASE_URL, '_top')
  const handlePrev = () => onChangeStep('password-generation')
  const handleNext = useCallback(() => {
    batch(() => {
      dispatch(
        onCheckGuestBusinessSuccess({
          entityType: '',
          isPublicOrg: 0,
          name: `${nameValue} `,
          dni: dniValue
        })
      )
      dispatch(
        onGetSellerBlockedInfoThunk({
          dni: dniValue,
          step: 'business-info',
          createTemporarySellerCallback: () => {
            dispatch(
              onCreateTemporarySellerThunk({
                address: addressValue,
                bypassNextStep: hasLanguage && isLanguageEN,
                country: countryValue,
                dni: dniValue,
                isForeign: hasLanguage,
                name: nameValue,
                nickname: nicknameValue,
                size: sizeValue,
                type: typeValue,
                callback: () => (isLanguageEN ? onChangeStep('complete') : onChangeStep('business-areas'))
              })
            )
          }
        })
      )
    })
  }, [
    dispatch,
    onGetSellerBlockedInfoThunk,
    onCheckGuestBusinessSuccess,
    onCreateTemporarySellerThunk,
    onChangeStep,
    addressValue,
    hasLanguage,
    isLanguageEN,
    countryValue,
    dniValue,
    hasLanguage,
    nameValue,
    nicknameValue,
    sizeValue,
    typeValue
  ])
  const disabledNext = useMemo(() => {
    const hadErrors =
      addressError !== '' || countryError !== '' || dniError !== '' || nameError !== '' || nicknameError !== ''
    const hasEmptyValues =
      addressValue === '' ||
      countryValue === '' ||
      dniValue === '' ||
      nameValue === '' ||
      nicknameValue === '' ||
      sizeValue === '' ||
      typeValue === ''

    const hasBusinessErrors = errors.businessInformationError !== null && errors.businessInformationError !== ''

    return hadErrors || hasEmptyValues || hasBusinessErrors || loading
  }, [
    addressError,
    addressValue,
    countryError,
    countryValue,
    dniError,
    dniValue,
    loading,
    nameError,
    nameValue,
    nicknameError,
    nicknameValue,
    sizeValue,
    typeValue,
    errors.businessInformationError
  ])

  const businessError = useMemo(() => {
    const ERROR_CONTENT = {
      BUSINESS_ALREADY_EXISTS_ERROR: {
        titleKey: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_modalTitle',
        text1Key: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_modalText_1',
        text2Key: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_modalText_2',
        buttonKey: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_actionButton',
        showInstructions: true,
        instructions1Key: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_instructions_1',
        instructions2Key: 'RegisterWizardForeignBusinessInformation__errorBusinessExists_instructions_2',
        handleOnClick: () => handleDesktopRedirect()
      },

      BUSINESS_IS_NOT_FOREIGNER_ERROR: {
        titleKey: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_modalTitle',
        text1Key: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_modalText_1',
        text2Key: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_modalText_2',
        buttonKey: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_actionButton',
        showInstructions: false,
        instructions1Key: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_instructions_1',
        instructions2Key: 'RegisterWizardForeignBusinessInformation__errorBusinessNational_instructions_2',
        handleOnClick: () => (window.location.href = `${REACT_APP_MERCADO_PUBLICO_PROVEEDOR_URL}/registrarme`)
      }
    }

    return ERROR_CONTENT[errors.businessInformationError] ?? null
  }, [errors.businessInformationError])

  useAnalyticsPageView('/paso/4/informacion_empresa')

  return (
    <Root>
      <Section item>
        <Title>{language('RegisterWizardForeignBusinessInformation__title')}</Title>
      </Section>

      <Section item margin="23px 0 33px" maxWidth="683px">
        <Instructions>{language('RegisterWizardForeignBusinessInformation__description')}</Instructions>
      </Section>

      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Input
            error={nameError}
            label={language('RegisterWizardForeignBusinessInformation__legalNameInputLabel')}
            value={nameValue}
            onChange={handleNameChange}
          />
        </Section>
        <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
          <Input
            error={nicknameError}
            label={language('RegisterWizardForeignBusinessInformation__nicknameInputLabel')}
            tooltip={language('RegisterWizardForeignBusinessInformation__nicknameTooltip')}
            value={nicknameValue}
            onChange={handleNicknameChange}
          />
        </Section>
      </Section>

      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Input
            error={dniError}
            info={language('RegisterWizardForeignBusinessInformation__dniInputInfo')}
            label={language('RegisterWizardForeignBusinessInformation__dniInputLabel')}
            tooltip={
              <>
                {language('RegisterWizardForeignBusinessInformation__dniInputTooltip__1')}
                <br />
                {language('RegisterWizardForeignBusinessInformation__dniInputTooltip__2')}
              </>
            }
            value={dniValue}
            onChange={handleDniChange}
          />
        </Section>
      </Section>

      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Select
            label={language('RegisterWizardForeignBusinessInformation__typeSelectLabel')}
            maxWidth="400px"
            options={businessTypeOptions}
            value={typeValue}
            onChange={handleTypeChange}
          />
        </Section>
        <Section item md={6} padding={!size.isDownToMedium && '0 0 0 21px'} xs={12}>
          <Select
            info={
              <>
                {language('RegisterWizardForeignBusinessInformation__sizeSelectInfo__1')}&nbsp;
                <ExternalLink
                  href="https://www.sii.cl/valores_y_fechas/index_valores_y_fechas.html"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {language('RegisterWizardForeignBusinessInformation__sizeSelectInfo__2')}
                </ExternalLink>
                .
              </>
            }
            label={language('RegisterWizardForeignBusinessInformation__sizeSelectLabel')}
            options={businessSizeOptions}
            value={sizeValue}
            onChange={handleSizeChange}
          />
        </Section>
      </Section>

      <Section container margin="3px 0 0" maxWidth="693px">
        <Section item md={6} padding={!size.isDownToMedium && '0 21px 0 0'} xs={12}>
          <Select
            error={countryError}
            label={language('RegisterWizardForeignBusinessInformation__countrySelectLabel')}
            maxWidth="400px"
            options={businessCountriesOptions}
            value={countryValue}
            onChange={handleCountryChange}
          />
        </Section>
      </Section>

      <Section container margin="3px 0 0" maxWidth="693px">
        <Input
          error={addressError}
          info={language('RegisterWizardForeignBusinessInformation__addressInputInfo')}
          label={language('RegisterWizardForeignBusinessInformation__addressInputLabel')}
          value={addressValue}
          onChange={handleAddressChange}
        />
      </Section>

      {!blockedInfo.loading && (
        <>
          {blockedInfo.error && (
            <Toast
              margin="16px 0"
              width="100%"
              severity="warning"
              textDirection="vertical"
              title="Información temporalmente no disponible"
            >
              <br />
              En estos momentos no podemos mostrar esta información debido a una intermitencia en los servicios del
              Servicio de Impuestos Internos. Por favor, vuelve a intentarlo en unos minutos.
            </Toast>
          )}

          {!blockedInfo.error && blockedInfo.isBlocked && (
            <Toast
              margin="16px 0"
              width="100%"
              severity="warning"
              title="Empresa registrada y bloqueada de Mercado Público"
            >
              <br />
              Nuestros registros indican que la empresa ya se encuentra registrada y está bloqueada en la plataforma de
              Mercado Público debido a: <Strong>{blockedInfo.reason}</Strong> Si necesitas más información, solicítala a
              través de nuestro{' '}
              <LinkHelp href={REACT_APP_HELP_URL} rel="noreferrer" target="_blank">
                Centro de Ayuda.
              </LinkHelp>
            </Toast>
          )}
        </>
      )}

      {!size.isTiny && (
        <Section container margin="52px 0 0 0">
          <DesktopNavigation>
            <Button type="button" variant="text" width="265px" disabled={blockedInfo.loading} onClick={handlePrev}>
              {language('RegisterWizardForeignBusinessInformation__backButton')}
            </Button>
            <Button
              color="primary"
              disabled={disabledNext || blockedInfo.loading}
              loading={loading}
              margin="0 0 0 30px"
              type="button"
              width="265px"
              onClick={handleNext}
            >
              {language('RegisterWizardForeignBusinessInformation__nextButton')}
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {size.isTiny && (
        <MobileNavigation>
          <Button type="button" variant="text" width="168px" disabled={blockedInfo.loading} onClick={handlePrev}>
            {language('RegisterWizardForeignBusinessInformation__backButton')}
          </Button>
          <Button
            color="primary"
            disabled={disabledNext || blockedInfo.loading}
            loading={loading}
            type="button"
            width="168px"
            onClick={handleNext}
          >
            {language('RegisterWizardForeignBusinessInformation__nextButton')}
          </Button>
        </MobileNavigation>
      )}

      <Dialog maxWidth="600px" open={businessError !== null} onClose={handleDialogClose}>
        <ModalContent>
          <WarningContainer>
            <WarningIcon />
          </WarningContainer>

          {/* Title */}
          <ModalTitle>{businessError && `${language(businessError?.titleKey)}`}</ModalTitle>

          {/* Text */}
          <ModalText>
            {businessError && (
              <>
                {language(businessError?.text1Key)}
                <br />
                {language(businessError?.text2Key)}
              </>
            )}
          </ModalText>

          <ModalAction>
            <Button
              color="default"
              type="button"
              width={size.isTiny ? '100%' : '349px'}
              onClick={businessError?.handleOnClick}
            >
              {language(businessError?.buttonKey)}
            </Button>
          </ModalAction>

          {businessError?.showInstructions && (
            <ModalInstructions>
              {businessError && (
                <>
                  {language(businessError?.instructions1Key)}
                  <ExternalLink
                    href={`${REACT_APP_HELP_URL}/autoatencion/secciondeayuda/articulo/?c=CAT-01003&s=CAT-010032&a=KA-01705`}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {language(businessError?.instructions2Key)}
                  </ExternalLink>
                  .
                </>
              )}
            </ModalInstructions>
          )}
        </ModalContent>
      </Dialog>
    </Root>
  )
}

RegisterWizardForeignBusinessInformation.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardForeignBusinessInformation.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardForeignBusinessInformation
